import type { ReactNode } from "react";

import { useIsMobile } from "@/hooks/use-mobile";

/**
 * Wraps a scrolling list with its pager — one row, compact at every width
 * (#382).
 *
 * Below `md` the pager is the last thing inside the scroll container, after
 * the last row, rather than pinned to the viewport: it costs no space until
 * someone scrolls to the end, the way Shopify's own mobile lists work. From
 * `md` up it stays a fixed sibling under the scroll container, because a
 * sticky table header depends on the table being the only thing that
 * scrolls.
 *
 * `DataTable` and the catalogue's card grid both scroll their own rows
 * rather than the page, so both place their pager through this rather than
 * each re-deciding where a pager belongs relative to its own scroll
 * container — that per-screen re-deriving is exactly the drift #382 found.
 */
export function ListWithFooter({
  footer,
  scrollClassName,
  children,
}: {
  /** Omitted (or falsy) renders no pager row at all — an empty list has none. */
  footer?: ReactNode;
  /**
   * Extra classes for the scrolling content itself, e.g. a card grid's
   * padding. Kept off the pager, which stays edge to edge, and off the
   * scroll container, so `scrollClassName`'s padding never keeps a mobile
   * pager from sitting flush against the container's own edge.
   */
  scrollClassName?: string;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  const bar = footer ? (
    <div className="border-t px-3 py-1">{footer}</div>
  ) : null;
  const content = scrollClassName ? (
    <div className={scrollClassName}>{children}</div>
  ) : (
    children
  );

  if (isMobile) {
    return (
      <div className="h-full overflow-auto">
        {content}
        {bar}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-auto">{content}</div>
      {bar}
    </div>
  );
}
